import { useQueryApplication } from 'src/graphqlhooks';
import { KIND_LOANAPP } from '../constants';
import { getNamespaceName } from '../objects/namespaces';
import { randomNumberRange } from 'src/utilities/advisor';
import { authenticatedUser } from 'src/utilities/storage';
import { returnStringIfNotEmpty } from 'src/utilities/hasValue';
import { requestGraphQL } from './useGraphQLQuery';
import { GQLQueryBuilder, QueryData } from './queryBuilder';

export const QUERY_CREATE_APPLICATION_ACTIVITIES = `insert_application_activities(objects: $applicationAudits) {
      returning {
        id,
        object_meta,
        type_meta,
        loanapp_id,
        action,
        purpose_of_action,
        event_actor,
        metadata
      }
    }`

export const CREATE_ACTIVITY_FOR_LOAN = `mutation createLoanActivity($applicationAudits: [application_activities_insert_input!]!) {
  ${QUERY_CREATE_APPLICATION_ACTIVITIES}
}`;

export const QUERY_CREATE_SYSTEM_ACTIVITIES = `insert_system_audit_activity(objects: $systemAudits) {
      returning {
        object_meta,
        loanapp_id,
        entity_id,
        entity_type,
        resource,
        actor,
        action,
        message,
        status,
        metadata,
        created_at
      }
    }`

export const CREATE_SYSTEM_ACTIVITY_FOR_LOAN = `mutation createSystemActivity($systemAudits: [system_audit_activity_insert_input!]!) {
  ${QUERY_CREATE_SYSTEM_ACTIVITIES}
}`;

export const CREATE_SYSTEM_AND_AUDIT_ACTIVITY_FOR_LOAN = `mutation createSystemAndAuditActivity(
  $systemAudits: [system_audit_activity_insert_input!]!,
  $applicationAudits: [application_activities_insert_input!]!) {
    ${QUERY_CREATE_SYSTEM_ACTIVITIES}
    ${QUERY_CREATE_APPLICATION_ACTIVITIES}
}`;

export const QUERY_DELETE_SYSTEM_ACTIVITY = `delete_system_audit_activity(where: {
    loanapp_id: {_eq: $loanId}, 
    entity_id: {_eq: $entityId}, 
    resource: {_eq: $resource}, 
    status: {_eq: $status}}
    ) {
    affected_rows
  }`

export const DELETE_SYSTEM_ACTIVITY_FOR_LOAN = `mutation deleteSystemActivity(
  $loanId: String, 
  $entityId: String,
  $resource: String, 
  $status: String) {
    ${QUERY_DELETE_SYSTEM_ACTIVITY}
}`;

export const UPDATE_SYSTEM_ACTIVITY_FOR_LOAN = `mutation update_system_audit_activity(
  $status:String!,
  $loanId: String, 
  $entityId: String,
  $resource: String
  ) {
    update_system_audit_activity(where: {
      resource: {_eq: $resource},
      loanapp_id :{_eq: $loanId},
      entity_id :{_eq: $entityId},
      }, _set: {status:$status}) {
  returning {
          metadata
          resource
          entity_id
          entity_type
          resource
          actor
          message
          action
          status
          created_at
          object_meta
          loanapp_id
      }
  }
}`

const GET_ACTIVITY_FOR_LOAN = `query describeActivitiesByLoan($loanId:bigint!) {
    application_activities(order_by: {created_at: desc} where: {loanapp_id: {_eq: $loanId}}) {
      object_meta
      type_meta
      metadata
      action,
      purpose_of_action,
      event_log,
      link_to_url,
      event_actor,
      created_at
    }
  }`;


export const mkApplicationActivity = (params: {
  loanapp_id: string,
  event_actor: string,
  action: string,
  purpose_of_action: string,
  event_log: string,
  link_to_url: string,
  metadata: any
}) => {
  const {
    loanapp_id,
    event_actor,
    action,
    purpose_of_action,
    event_log,
    link_to_url,
    metadata,
  } = params;
  return {
    type_meta: {
      kind: 'ApplicationActivity',
      api_version: 'v1',
    },
    object_meta: {
      name: `application-activity-${randomNumberRange(100000)}`,
      account: returnStringIfNotEmpty(authenticatedUser.accountId.get()),
      labels: {},
      annotations: {},
      owner_references: [
        {
          kind: KIND_LOANAPP,
          api_version: 'v1',
          name: loanapp_id,
          uid: loanapp_id,
          block_owner_deletion: false,
        },
      ],
      namespace: getNamespaceName(),
    },
    loanapp_id,
    event_actor,
    action,
    purpose_of_action,
    event_log,
    link_to_url,
    metadata,
  };
}

export const GQLCreateActivityInLoan = (
  loanapp_id: string,
  event_actor: string,
  action: string,
  purpose_of_action: string,
  event_log: string,
  link_to_url: string,
  dataCheck: any,
  metadata: any
) => {
  const activity = mkApplicationActivity({
    loanapp_id,
    event_actor,
    action,
    purpose_of_action,
    event_log,
    link_to_url,
    metadata,
  })
  return dataCheck({ variables: { applicationAudits: [activity] } });
};

export const GQLCreateSystemApplicationActivityInLoan = (
  applicationActivity: any,
  systemActivity: any,
  dataCheck: any
) => {
  return dataCheck({ variables: { systemAudits: [systemActivity], applicationAudits: [applicationActivity] } });
};

export const mkSystemAuditActivity = (params: {
  loanapp_id: string,
  entity_id: string,
  entity_type: string,
  resource: string,
  actor: string,
  action: string,
  status: string,
  message: string,
  metadata: any
}) => {
  const {
    loanapp_id,
    entity_id,
    entity_type,
    resource,
    action,
    actor,
    status,
    message,
    metadata,
  } = params;
  return {
    object_meta: {
      name: `activity-${randomNumberRange(100000)}`,
      account: returnStringIfNotEmpty(authenticatedUser.accountId.get()),
      labels: {},
      annotations: {},
      owner_references: [
        {
          kind: KIND_LOANAPP,
          api_version: 'v1',
          name: loanapp_id,
          uid: loanapp_id,
          block_owner_deletion: false,
        },
      ],
      namespace: getNamespaceName(),
    },
    loanapp_id,
    entity_id,
    entity_type,
    resource,
    action,
    actor,
    status,
    message,
    metadata,
  };
}

export const GQLCreateSystemActivityInLoan = (
  loanapp_id: string,
  entity_id: string,
  entity_type: string,
  resource: string,
  actor: string,
  action: string,
  status: string,
  message: string,
  metadata: any,
  dataCheck: any
) => {
  const activity = mkSystemAuditActivity({
    loanapp_id,
    entity_id,
    entity_type,
    resource,
    action,
    actor,
    status,
    message,
    metadata,
  })
  return dataCheck({ variables: { systemAudits: [activity] } }).then();
};

export const GQLGetActivitiesByLoan = (loanId: string) => {
  const { data, loading } = useQueryApplication(GET_ACTIVITY_FOR_LOAN, {
    variables: { loanId: loanId },
  });
  return { data, loading };
};
/*
this graphql used to create the feed audit with account_id
*/
export const CREATE_FEED_AUDIT = `mutation createFeedAudit($data: [feed_activities_insert_input!]!) {
    insert_feed_activities(objects: $data) {
      returning {
        id,
        object_meta,
        type_meta,
        account_id,
        action,
        purpose_of_action,
        event_actor,
        metadata
      }
    }
  }`;
/*
this graphql used to get last recent 10  feed audit data given advisor account id and namespace
*/
const GET_FEED_ACTIVITY = `feed_activities(limit: 10, order_by: {created_at: desc} ,where: {account_id: {_eq: $accountId} ,_and:{object_meta: {_contains: $objectMeta}}} ) {
    id
    metadata
    updated_at
    created_at
    account_id
    action,
    link_to_url
  }
`;
/*
method used to get the feed audit data given account id and namespace
*/
export const GQLGetFeedActivities = (data: {
  accountId: any,
  namespace: string,
  onSuccess?: (_: any) => void,
  onError?: (_: any) => void,
}) => {
  const { accountId, namespace, onSuccess, onError } = data;
  const feedGetQuery: QueryData = {
    name: 'get_feed_audits',
    query: GET_FEED_ACTIVITY,
    variables: {
      accountId,
      objectMeta: { namespace }
    }
  }
  const queryBuilder = new GQLQueryBuilder([feedGetQuery], 'getFeedAudits')
  return requestGraphQL(
    queryBuilder.getQueryString(),
    queryBuilder.getQueryVariables()
  ).then((data: any) => {
    onSuccess && onSuccess(data);
    return data
  }).catch((err: any) => {
    onError && onError(err)
  })
}
